import React, { useState } from 'react';
import { useTelegram } from './TelegramProvider';
import { useTon } from './TelegramTonProvider';
import { PRESALE_STAGES, getCurrentStage, formatLilape } from './config/gameConfig';
import { PRIZE_POOL_ADDRESS } from './config';
import { recordTonSpent } from './hooks/useMultiplier';

export const PresalePage = () => {
  const { hapticFeedback, showAlert } = useTelegram();
  const { connected, sendTransaction, connect } = useTon();

  const [tonAmount, setTonAmount] = useState('1');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [purchasedAmount, setPurchasedAmount] = useState(0);

  const currentStage = getCurrentStage();
  const lilapeAmount = parseFloat(tonAmount || 0) * currentStage.rate;

  const handlePurchase = async () => {
    if (!connected) {
      connect?.();
      return;
    }

    const amount = parseFloat(tonAmount);
    if (amount < 0.1) {
      showAlert?.('Minimum purchase: 0.1 TON');
      return;
    }

    setIsProcessing(true);
    hapticFeedback?.('light');

    try {
      const result = await sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 300,
        messages: [{
          address: PRIZE_POOL_ADDRESS,
          amount: String(Math.floor(amount * 1e9)),
        }],
      });

      if (result?.boc) {
        // Record for VIP tracking
        recordTonSpent(amount);

        setPurchasedAmount(amount * currentStage.rate);
        setShowSuccess(true);
        hapticFeedback?.('heavy');
      }
    } catch (err) {
      if (!err.message?.includes('rejected') && !err.message?.includes('cancelled')) {
        showAlert?.(`Error: ${err.message}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  if (showSuccess) {
    return (
      <div style={{ padding: '40px 16px', textAlign: 'center' }}>
        <div style={{ fontSize: 60, marginBottom: 20 }}>🎉</div>
        <div style={{ fontSize: 24, fontWeight: 900, color: '#fff', marginBottom: 8 }}>
          Purchase Successful!
        </div>
        <div style={{ fontSize: 32, fontWeight: 900, color: '#10B981', marginBottom: 16 }}>
          +{formatLilape(purchasedAmount)} LILAPE
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 24 }}>
          Tokens will be sent to your wallet
        </div>
        <button
          onClick={() => setShowSuccess(false)}
          style={{
            padding: '14px 32px',
            borderRadius: 12,
            background: 'linear-gradient(135deg, #10B981, #059669)',
            border: 'none',
            color: '#fff',
            fontSize: 16,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Buy More
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 16px 100px' }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-icon" style={{ background: 'linear-gradient(135deg, #F59E0B, #EF4444)' }}>🚀</div>
        <div>
          <div className="page-title">Presale</div>
          <div className="page-subtitle">Buy LILAPE at discount</div>
        </div>
      </div>

      {/* Current Stage Highlight */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(239,68,68,0.1))',
        borderRadius: 20,
        padding: 20,
        border: '2px solid rgba(245,158,11,0.4)',
        marginBottom: 16,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Animated background */}
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'radial-gradient(circle at 80% 20%, rgba(245,158,11,0.2), transparent 50%)',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>CURRENT STAGE</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: '#FFD700' }}>Stage {currentStage.stage}</div>
            </div>
            <div style={{
              padding: '8px 16px',
              borderRadius: 30,
              background: 'linear-gradient(135deg, #10B981, #059669)',
              fontSize: 14,
              fontWeight: 800,
              color: '#fff',
              animation: 'pulse 2s ease-in-out infinite',
            }}>
              {currentStage.discount}% OFF
            </div>
          </div>

          <div style={{
            background: 'rgba(0,0,0,0.3)',
            borderRadius: 14,
            padding: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>RATE</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#fff' }}>1 TON = {currentStage.rateLabel}</div>
            </div>
            <div style={{ fontSize: 40 }}>💎</div>
          </div>
        </div>
      </div>

      {/* Purchase Form */}
      <div style={{
        background: 'var(--bg-card)',
        borderRadius: 16,
        padding: 20,
        border: '1px solid var(--border-color)',
        marginBottom: 16,
      }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>Amount in TON</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[0.5, 1, 5, 10, 50].map(amt => (
              <button
                key={amt}
                onClick={() => setTonAmount(String(amt))}
                style={{
                  flex: 1,
                  padding: '10px 8px',
                  borderRadius: 10,
                  background: tonAmount === String(amt) ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${tonAmount === String(amt) ? 'rgba(59,130,246,0.5)' : 'rgba(255,255,255,0.1)'}`,
                  color: tonAmount === String(amt) ? '#3B82F6' : '#fff',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {amt}
              </button>
            ))}
          </div>
          <input
            type="number"
            value={tonAmount}
            onChange={(e) => setTonAmount(e.target.value)}
            min="0.1"
            step="0.1"
            style={{
              width: '100%',
              marginTop: 10,
              padding: '14px 16px',
              borderRadius: 12,
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff',
              fontSize: 18,
              fontFamily: 'monospace',
              outline: 'none',
            }}
            placeholder="Enter amount"
          />
        </div>

        <div style={{
          background: 'rgba(16,185,129,0.1)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>You will receive</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: '#10B981' }}>
            {formatLilape(lilapeAmount)} LILAPE
          </div>
          {currentStage.discount > 0 && (
            <div style={{ fontSize: 11, color: '#FFD700', marginTop: 4 }}>
              💰 Saving {currentStage.discount}% vs listing price
            </div>
          )}
        </div>

        <button
          onClick={handlePurchase}
          disabled={isProcessing || parseFloat(tonAmount) < 0.1}
          style={{
            width: '100%',
            padding: '16px',
            borderRadius: 14,
            background: isProcessing || parseFloat(tonAmount) < 0.1
              ? '#333'
              : 'linear-gradient(135deg, #F59E0B, #EF4444)',
            border: 'none',
            color: isProcessing ? '#666' : '#fff',
            fontSize: 16,
            fontWeight: 800,
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            boxShadow: isProcessing ? 'none' : '0 4px 20px rgba(245,158,11,0.4)',
          }}
        >
          {isProcessing ? '⏳ Processing...' : connected ? `🚀 Buy for ${tonAmount} TON` : '🔗 Connect Wallet'}
        </button>

        <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginTop: 10 }}>
          Minimum: 0.1 TON • Instant delivery
        </div>
      </div>

      {/* All Stages Preview */}
      <div style={{
        background: 'var(--bg-card)',
        borderRadius: 16,
        padding: 16,
        border: '1px solid var(--border-color)',
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#fff' }}>
          📈 Presale Roadmap
        </div>

        {PRESALE_STAGES.slice(0, 5).map((stage, i) => (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 0',
            borderBottom: i < 4 ? '1px solid rgba(255,255,255,0.05)' : 'none',
            opacity: stage.status === 'active' ? 1 : 0.5,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: stage.status === 'active' ? 'linear-gradient(135deg, #F59E0B, #EF4444)' : 'rgba(255,255,255,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 900, color: stage.status === 'active' ? '#fff' : '#888',
              }}>
                {stage.stage}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>1 TON = {stage.rateLabel}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  {stage.discount > 0 ? `${stage.discount}% discount` : 'Listing price'}
                </div>
              </div>
            </div>
            <div style={{
              fontSize: 11,
              fontWeight: 700,
              color: stage.status === 'active' ? '#10B981' : 'var(--text-muted)',
            }}>
              {stage.status === 'active' ? '● LIVE' : stage.status === 'listing' ? '◐ LIST' : '○ SOON'}
            </div>
          </div>
        ))}

        <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--text-muted)', fontSize: 12 }}>
          +5 more stages → Listing at 1 TON = 2.5B
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default PresalePage;
